import { useNavigate } from "react-router-dom";
import { axiosProtected } from "../../api/axios";
import { useAuthContext } from "../../_helper/Auth";
import { useRefreshToken } from "./useRefreshToken";
import { useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  readPasswordChangeRequiredFromPayload,
  readEmailVerificationFromPayload,
} from "../authPayloadGates";
import { MANDATORY_CHANGE_PASSWORD_PATH } from "../../Routes/PrivateRoute";

export function useAxios() {
  const {
    token,
    addToken,
    removeToken,
    removeUser,
    setEmailVerificationPending,
    setPasswordChangeRequired,
    setIsInternelServerError,
    setIsForbidden,
  } = useAuthContext();
  const refreshToken = useRefreshToken();
  const navigate = useNavigate();
  const location = useLocation();
  const refreshPromiseRef = useRef(null);
  const { i18n } = useTranslation();

  useEffect(() => {
    const reqInterceptor = axiosProtected.interceptors.request.use(
      (config) => {
        if (!config.headers?.Authorization && !!token.access_token) {
          config.headers.Authorization = `Bearer ${token.access_token}`;
          if (!config.headers.Accept && config.responseType !== "blob") {
            config.headers.Accept = "application/json";
          }
          config.headers["Accept-Language"] =
            (i18n.language === "ae" ? "ar" : "en") || "ar";
        }
        return config;
      },
      (err) => Promise.reject(err)
    );

    const resInterceptor = axiosProtected.interceptors.response.use(
      (res) => res,
      async (err) => {
        const originalReq = err?.config;

        if (err?.response?.status === 500) {
          setIsInternelServerError(true);
          return Promise.reject(err);
        }

        if (err?.response?.status === 403) {
          setIsForbidden(true);
          return Promise.reject(err);
        }

        if (
          err.code === "ECONNABORTED" ||
          err.message === "timeout of 30000ms exceeded"
        ) {
          console.error("Request timeout:", err.config?.url);
          return Promise.reject(
            new Error(
              "Request timeout. Please check your connection and try again."
            )
          );
        }

        if (err.message === "Network Error" || !err.response) {
          console.error("Network error:", err);
          return Promise.reject(
            new Error("Network error. Please check your connection.")
          );
        }

        if (
          err?.response?.status === 401 &&
          !originalReq._retry &&
          !originalReq.url.includes("refresh")
        ) {
          originalReq._retry = true;

          if (!refreshPromiseRef.current) {
            refreshPromiseRef.current = refreshToken(token)
              .then((newTokens) => {
                if (!newTokens?.access_token) {
                  throw new Error("Missing access token from refresh response");
                }

                addToken({
                  access_token: newTokens.access_token,
                  refresh_token: newTokens.refresh_token,
                });

                const emailPending = readEmailVerificationFromPayload(newTokens);
                if (emailPending !== undefined) {
                  setEmailVerificationPending(emailPending);
                }

                const pwdRequired =
                  readPasswordChangeRequiredFromPayload(newTokens);
                if (pwdRequired !== undefined) {
                  setPasswordChangeRequired(pwdRequired);
                }

                if (pwdRequired === true) {
                  if (location.pathname !== MANDATORY_CHANGE_PASSWORD_PATH) {
                    navigate(MANDATORY_CHANGE_PASSWORD_PATH, { replace: true });
                  }
                } else if (emailPending === true && location.pathname !== "/verify-email") {
                  navigate("/verify-email", { replace: true });
                }

                return newTokens;
              })
              .catch((refreshErr) => {
                if (refreshErr?.response?.status === 401) {
                  removeToken(null);
                  removeUser(null);
                  navigate("/login", { state: { from: location.pathname } });
                }

                throw refreshErr;
              })
              .finally(() => {
                refreshPromiseRef.current = null;
              });
          }

          try {
            const newTokens = await refreshPromiseRef.current;

            originalReq.headers = originalReq.headers || {};
            originalReq.headers.Authorization = `Bearer ${newTokens.access_token}`;

            return await axiosProtected(originalReq);
          } catch (refreshErr) {
            removeToken(null);
            removeUser(null);
            navigate("/login", { state: { from: location.pathname } });
            return Promise.reject(refreshErr);
          }
        }
        return Promise.reject(err);
      }
    );

    return () => {
      axiosProtected.interceptors.request.eject(reqInterceptor);
      axiosProtected.interceptors.response.eject(resInterceptor);
    };
  }, [
    token,
    addToken,
    removeToken,
    removeUser,
    setEmailVerificationPending,
    setPasswordChangeRequired,
    navigate,
    location.pathname,
    refreshToken,
    i18n.language,
    setIsForbidden,
    setIsInternelServerError,
  ]);

  return { axiosProtected };
}
